import {
  Box,
  Card,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Edited } from '~/src/components/Inputs/Edit';
import {
  ProjectStackField,
  ProjectStackDocument,
} from '~/src/entities/project';
import { useFieldMappingOverride } from '~/src/entities/fieldMapping';
import type { FieldMappingOverrideSubject } from '~/src/entities/fieldMapping/hooks/useFieldMappingOverride';
import { FieldRenderProps } from '..';
import { FieldHeaderWithEdit } from '~/src/pages/Populate/PopulatePageV2/PopulatePageContent/PopulateCardField/FieldHeader/FieldHeaderWithEdit';
import { OptionLabel } from '~/src/pages/Populate/PopulatePageV2/PopulatePageContent/PopulateCardField/SingleMultipleFieldHelper/OptionLabel';
import {
  appendPicklistOptions,
  MultipleChoiceOptionWithPicklistMeta,
  truncateLabel,
} from '../SingleMultipleFieldHelper/optionUtils';

export type SingleSelectFieldProps = {
  field: FieldRenderProps;
  projectField: ProjectStackField;
  projectStackDocuments: ProjectStackDocument[];
  hasActiveMapping?: boolean;
  fieldMappingOverrideSubject?: FieldMappingOverrideSubject;
};

const getOptionValue = (o: MultipleChoiceOptionWithPicklistMeta) =>
  o.formatted ? o.option : o.value;

const findSelectedOption = (
  options: MultipleChoiceOptionWithPicklistMeta[],
  selectedValue: string | undefined,
) =>
  options.find((o) => {
    const optionValue = getOptionValue(o) ?? '';
    return optionValue === (selectedValue ?? '');
  });

const appendValueOptionIfMissing = (
  options: MultipleChoiceOptionWithPicklistMeta[],
  value: string | undefined,
) => {
  const normalizedValue = value ?? '';
  if (!normalizedValue) return options;

  const isValueInOptions = options.some(
    (o) => (getOptionValue(o) ?? '') === normalizedValue,
  );
  if (isValueInOptions) return options;

  return [
    ...options,
    {
      option: truncateLabel(normalizedValue),
      value: normalizedValue,
      formatted: false,
    },
  ];
};

export const SingleSelectField = React.memo(
  ({
    field,
    projectField,
    projectStackDocuments,
    hasActiveMapping,
    fieldMappingOverrideSubject,
  }: SingleSelectFieldProps) => {
    const { value, onChange, ref, onBlur, onFocus, countTouched } = field;

    const fieldMappingId =
      projectField.field_mapping_metadata?.field_mapping_id;
    const fieldMappingSourceName =
      projectField.field_mapping_metadata?.source_field;

    const { triggerOverride, triggerTextOverrideDebounced } =
      useFieldMappingOverride({
        hasActiveMapping,
        fieldMappingOverrideSubject,
        fieldMappingId,
        fieldMappingSourceName,
      });

    const projectMultipleChoiceVariableOptions =
      projectField.multiple_choice_variable?.options;
    const sourceFieldPicklistOptions =
      projectField.field_mapping_metadata?.source_field_picklist_options;
    const staticPicklistOptions = projectField.picklist_options;
    const hasStaticPicklist =
      !!staticPicklistOptions && staticPicklistOptions.length > 0;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const optionsFromDraft = hasStaticPicklist
      ? staticPicklistOptions
      : projectMultipleChoiceVariableOptions ?? [];

    // Auto-pick the first option once per picklist instance; avoids
    // re-firing during the brief field.value='' window on detach.
    const autoPickedForRef = useRef<typeof staticPicklistOptions>(undefined);
    useEffect(() => {
      const firstOption = staticPicklistOptions?.[0];
      if (
        hasStaticPicklist &&
        firstOption &&
        !field.value &&
        autoPickedForRef.current !== staticPicklistOptions
      ) {
        autoPickedForRef.current = staticPicklistOptions;
        onChange(firstOption.value);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasStaticPicklist, staticPicklistOptions, field.value]);

    // "Edited" should reflect the original draft options, not any locally appended option.
    const isEdited =
      !!field.value && !findSelectedOption(optionsFromDraft, value);

    const [options, setOptions] = useState<
      MultipleChoiceOptionWithPicklistMeta[]
    >(() => optionsFromDraft);
    const [isEditing, setIsEditing] = useState(false);

    const optionsWithPicklist = useMemo(() => {
      if (!hasActiveMapping) return optionsFromDraft;
      return appendPicklistOptions(
        optionsFromDraft,
        sourceFieldPicklistOptions,
      );
    }, [optionsFromDraft, hasActiveMapping, sourceFieldPicklistOptions]);

    useEffect(() => {
      // Change the options and selected option when edit is finished (Save & Close is clicked)
      if (isEditing) {
        return;
      }

      const displayOptions = appendValueOptionIfMissing(
        optionsWithPicklist,
        value,
      );
      setOptions(displayOptions);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, isEditing, optionsWithPicklist]);

    return (
      <Card
        sx={(theme) => ({
          border: `1px solid ${
            isEditing ? theme.palette.border : 'transparent'
          }`,
          marginBottom: theme.spacing(3),
        })}
        elevation={isEditing ? 3 : 0}
      >
        <Box p={isEditing ? 2 : 0}>
          <FieldHeaderWithEdit
            projectField={projectField}
            projectStackDocuments={projectStackDocuments}
            isEdited={isEdited}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />

          {hasStaticPicklist ||
          projectField.single_select_display_type === 'Dropdown' ? (
            <Select
              inputRef={ref}
              inputProps={{ id: field.name, 'data-testid': 'select-input' }}
              id={field.name}
              data-testid="select"
              labelId={field.name}
              value={field.value ? field.value : ''}
              sx={{ display: 'block' }}
              size={'small'}
              onBlur={onBlur}
              onFocus={onFocus}
              onChange={(e) => {
                onChange(e.target.value);
                if (countTouched) {
                  countTouched(field.name);
                }
                triggerOverride();
              }}
            >
              {options.map(
                ({ value, option, formatted, isAppendedPicklist }) => {
                  return (
                    <MenuItem key={value} value={formatted ? option : value}>
                      <OptionLabel
                        label={option}
                        showPicklistIcon={!!isAppendedPicklist}
                      />
                    </MenuItem>
                  );
                },
              )}
            </Select>
          ) : (
            <RadioGroup
              name={field.name}
              value={field.value}
              onChange={(_, v) => {
                onChange(v);
                if (countTouched) {
                  countTouched(field.name);
                }
                triggerOverride();
              }}
              id={field.name}
            >
              {options.map((fieldOption, i) => {
                let optionValue = fieldOption.formatted
                  ? fieldOption.option
                  : fieldOption.value;

                optionValue = optionValue === null ? '' : optionValue;
                return (
                  <Box pb={1} key={fieldOption.value}>
                    <FormControlLabel
                      style={{ display: 'flex' }}
                      slotProps={{ typography: { variant: 'body1' } }}
                      checked={field.value === optionValue}
                      control={
                        <Radio
                          onBlur={onBlur}
                          onFocus={onFocus}
                          inputRef={i === 0 ? ref : undefined}
                          sx={{ paddingTop: 0, paddingBottom: 0 }}
                        />
                      }
                      label={
                        <OptionLabel
                          label={fieldOption.option}
                          showPicklistIcon={!!fieldOption.isAppendedPicklist}
                        />
                      }
                      value={optionValue}
                    />
                  </Box>
                );
              })}
            </RadioGroup>
          )}

          {isEditing ? (
            <Box pt={5}>
              <Box pb={1}>
                <Typography variant={'label'}>Text to Merge</Typography>
              </Box>
              <TextField
                id={field.name}
                inputProps={{ 'data-testid': 'text-to-merge-field' }}
                size={'small'}
                value={value}
                onChange={(e) => {
                  onChange(e.target.value);
                  if (countTouched) {
                    countTouched(field.name);
                  }
                  triggerTextOverrideDebounced();
                }}
                onBlur={onBlur}
                onFocus={onFocus}
                fullWidth
                multiline
              />
              <Box
                py={1}
                visibility={isEdited ? 'visible' : 'hidden'}
                display={'flex'}
                justifyContent={'flex-end'}
                alignItems={'center'}
              >
                {isEdited ? <Edited /> : null}
              </Box>
            </Box>
          ) : null}
        </Box>
      </Card>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.field.value === nextProps.field.value &&
      prevProps.projectField === nextProps.projectField &&
      prevProps.projectStackDocuments === nextProps.projectStackDocuments &&
      prevProps.hasActiveMapping === nextProps.hasActiveMapping
    );
  },
);
